import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FaTimes, FaFilePdf, FaSearch, FaEye, FaUpload } from 'react-icons/fa';
import {
  useAddTestKitchenItemMutation,
  useUpdateTestKitchenItemMutation,
  useGetAllTestKitchenTagsQuery,
} from '../Services/SecondaryApi';
import Swal from 'sweetalert2';
import TagInput from './TagInput';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import '../Styles/AddTestKitchenItemModal.css';

// FSANZ standard 14 allergens — fixed by law, never edited via UI.
const FSANZ_14 = [
  'Gluten', 'Crustacea', 'Egg', 'Fish', 'Milk / dairy', 'Peanut', 'Soy',
  'Sesame', 'Tree nuts', 'Lupin', 'Molluscs', 'Mustard', 'Celery', 'Sulphites',
];

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  disclaimer: yup.string().nullable(),
  status: yup.boolean().default(true),
  pdf_file: yup.mixed()
    .test('fileSize', 'PDF file size is too large (max 10MB)', (value) => {
      if (!value) return true;
      return value.size <= 10 * 1024 * 1024;
    })
    .test('fileType', 'Only PDF files are allowed', (value) => {
      if (!value) return true;
      return value.type === 'application/pdf';
    }),
  item_image: yup.mixed()
    .test('fileSize', 'Image file size is too large (max 5MB)', (value) => {
      if (!value) return true;
      return value.size <= 5 * 1024 * 1024;
    })
    .test('fileType', 'Only JPG, PNG, and GIF images are allowed', (value) => {
      if (!value) return true;
      return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
    }),
  allergens: yup.array().of(yup.string().oneOf(FSANZ_14)).nullable(),
  dietary_tags: yup.array().of(yup.object().shape({ id: yup.number().required(), name: yup.string().required() })),
  operational_tags: yup.array().of(yup.object().shape({ id: yup.number().required(), name: yup.string().required() })),
  service_tags: yup.array().of(yup.object().shape({ id: yup.number().required(), name: yup.string().required() })),
  seasonal_tags: yup.array().of(yup.object().shape({ id: yup.number().required(), name: yup.string().required() })),
});

const AddTestKitchenItemModal = ({ show, onHide, item, onSave }) => {
  const isEditing = !!item;
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [disclaimerValue, setDisclaimerValue] = useState('');
  const [existingPdf, setExistingPdf] = useState(null);
  const [existingImage, setExistingImage] = useState(null);

  const { data: allTags = {}, isLoading: isLoadingTags } = useGetAllTestKitchenTagsQuery(undefined, {
    skip: !show,
  });

  const { dietary = [], operational = [], service = [], seasonal = [] } = allTags.data || {};

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '', disclaimer: '', status: true, allergens: [],
      dietary_tags: [], operational_tags: [], service_tags: [], seasonal_tags: [],
    },
  });

  const watchTags = {
    dietary: watch('dietary_tags') || [],
    operational: watch('operational_tags') || [],
    service: watch('service_tags') || [],
    seasonal: watch('seasonal_tags') || [],
  };
  const watchAllergens = watch('allergens') || [];

  useEffect(() => {
    if (item) {
      if (item.pdf_url || item.presigned_pdf_url) {
        setExistingPdf(item.pdf_url);
        setPdfPreview(item.presigned_pdf_url || item.pdf_url);
      }
      if (item.item_image || item.presigned_item_image_url) {
        setExistingImage(item.item_image);
        setImagePreview(item.presigned_item_image_url || item.item_image);
      }
      setDisclaimerValue(item.disclaimer || '');
      reset({
        name: item.name || '',
        disclaimer: item.disclaimer || '',
        status: item.status ?? true,
        allergens: item.allergens || [],
        dietary_tags: item.dietary_tags || [],
        operational_tags: item.operational_tags || [],
        service_tags: item.service_tags || [],
        seasonal_tags: item.seasonal_tags || [],
      });
    } else {
      reset({
        name: '', disclaimer: '', status: true, allergens: [],
        dietary_tags: [], operational_tags: [], service_tags: [], seasonal_tags: [],
      });
      setDisclaimerValue('');
      setPdfFile(null); setPdfPreview(null);
      setImageFile(null); setImagePreview(null);
      setExistingPdf(null); setExistingImage(null);
    }
  }, [item, show, reset]);

  const handlePdfFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') { setError('Only PDF files are allowed'); return; }
      if (file.size > 10 * 1024 * 1024) { setError('PDF file size should be less than 10MB'); return; }
      setPdfFile(file);
      setPdfPreview(URL.createObjectURL(file));
      setValue('pdf_file', file, { shouldValidate: true });
      setError('');
    } else {
      setPdfFile(null);
      setPdfPreview(existingPdf || null);
      setValue('pdf_file', null, { shouldValidate: true });
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) { setError('Only JPG, PNG, and GIF images are allowed'); return; }
      if (file.size > 5 * 1024 * 1024) { setError('Image size should be less than 5MB'); return; }
      const newFile = new File([file], file.name, { type: file.type });
      setImageFile(newFile);
      setImagePreview(URL.createObjectURL(newFile));
      setValue('item_image', newFile, { shouldValidate: true });
      setError('');
    } else {
      setImageFile(null);
      setImagePreview(existingImage || null);
      setValue('item_image', null, { shouldValidate: true });
    }
  };

  const handleTagSelect = (type) => (tag) => {
    const current = watch(type) || [];
    if (!current.some(t => t.id === tag.id)) setValue(type, [...current, tag]);
  };

  const handleTagRemove = (type) => (tagId) => {
    setValue(type, (watch(type) || []).filter(tag => tag.id !== tagId));
  };

  const handleSearch = (type) => (query) => {
    const map = {
      dietary_tags: dietary,
      operational_tags: operational,
      service_tags: service,
      seasonal_tags: seasonal,
    };
    const pool = map[type] || [];
    if (!query) return pool;
    const q = query.toLowerCase();
    return pool.filter(tag =>
      tag.name.toLowerCase().includes(q) ||
      (tag.description && tag.description.toLowerCase().includes(q))
    );
  };

  const handleAllergenToggle = (allergen) => {
    const current = watch('allergens') || [];
    const next = current.includes(allergen)
      ? current.filter(a => a !== allergen)
      : [...current, allergen];
    setValue('allergens', next);
  };

  const removePdf = () => {
    setPdfFile(null); setPdfPreview(existingPdf ? '' : null);
    setValue('pdf_file', null, { shouldValidate: true });
    const el = document.getElementById('pdf_file');
    if (el) el.value = '';
  };

  const removeImage = () => {
    setImageFile(null); setImagePreview(existingImage ? '' : null);
    setValue('item_image', null, { shouldValidate: true });
    const el = document.getElementById('item_image');
    if (el) el.value = '';
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('name', data.name || '');
      formData.append('disclaimer', data.disclaimer || '');
      formData.append('status', data.status ? '1' : '0');

      // Allergens
      (data.allergens || []).forEach(a => formData.append('allergens[]', a));

      // Tags
      ['dietary', 'operational', 'service', 'seasonal'].forEach(type => {
        const key = `${type}_tags`;
        (Array.isArray(data[key]) ? data[key] : [])
          .map(tag => typeof tag === 'object' ? tag.id : tag)
          .forEach(id => formData.append(`${key}[]`, id));
      });

      // PDF
      if (pdfFile instanceof File) {
        formData.append('pdf', pdfFile);
      } else if (pdfPreview === '' && existingPdf) {
        formData.append('pdf_url', '');
      } else if (existingPdf) {
        formData.append('pdf_url', existingPdf);
      }

      // Image
      if (imageFile instanceof File) {
        formData.append('item_image', imageFile);
      } else if (imagePreview === '' && existingImage) {
        formData.append('item_image', '');
      } else if (existingImage) {
        formData.append('item_image', existingImage);
      }

      await onSave(formData);
      onHide();
    } catch (err) {
      setError(err.message || 'Failed to save test kitchen item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Body>
        <h4>{isEditing ? 'Edit Test Kitchen Item' : 'Add New Test Kitchen Item'}</h4>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            {/* Name */}
            <Col xs={12}>
              <div className="form-control">
                <label>Name <span className="red-star">*</span></label>
                <br />
                <input
                  type="text"
                  placeholder="Enter item name"
                  className={errors.name ? 'is-invalid' : ''}
                  {...register('name')}
                />
                {errors.name && <p className="error-text">{errors.name.message}</p>}
              </div>
            </Col>

            {/* Disclaimer */}
            <Col xs={12}>
              <div className="form-control">
                <div className="d-flex align-items-center mb-2">
                  <label className="me-2">Disclaimer</label>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-disclaimer">Add a disclaimer that will be shown with this item</Tooltip>}
                  >
                    <span className="info-icon"><FaEye /></span>
                  </OverlayTrigger>
                </div>
                <CKEditor
                  editor={ClassicEditor}
                  data={disclaimerValue}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setDisclaimerValue(data);
                    setValue('disclaimer', data);
                  }}
                  disabled={isSubmitting}
                />
              </div>
            </Col>

            {/* Dietary Tags */}
            <Col xs={12}>
              <TagInput
                label="Dietary Tags"
                selectedTags={watchTags.dietary}
                onTagSelect={handleTagSelect('dietary_tags')}
                onTagRemove={handleTagRemove('dietary_tags')}
                onSearch={handleSearch('dietary_tags')}
                isLoading={isLoadingTags}
                placeholder="Search dietary tags..."
              />
            </Col>

            {/* Operational Tags */}
            <Col xs={12}>
              <TagInput
                label="Operational Tags"
                selectedTags={watchTags.operational}
                onTagSelect={handleTagSelect('operational_tags')}
                onTagRemove={handleTagRemove('operational_tags')}
                onSearch={handleSearch('operational_tags')}
                isLoading={isLoadingTags}
                placeholder="Search operational tags..."
              />
            </Col>

            {/* Meal Tags — facet is named "Meal" in the spec; code name stays `service` (label-only rename). */}
            <Col xs={12}>
              <TagInput
                label="Meal"
                selectedTags={watchTags.service}
                onTagSelect={handleTagSelect('service_tags')}
                onTagRemove={handleTagRemove('service_tags')}
                onSearch={handleSearch('service_tags')}
                isLoading={isLoadingTags}
                placeholder="Search meal tags..."
              />
            </Col>

            {/* Seasonal Tags */}
            <Col xs={12}>
              <TagInput
                label="Seasonal Tags"
                selectedTags={watchTags.seasonal}
                onTagSelect={handleTagSelect('seasonal_tags')}
                onTagRemove={handleTagRemove('seasonal_tags')}
                onSearch={handleSearch('seasonal_tags')}
                isLoading={isLoadingTags}
                placeholder="Search seasonal tags..."
              />
            </Col>

            {/* Allergens present — FSANZ 14, fixed list, tap-to-toggle chips */}
            <Col xs={12}>
              <div className="form-control">
                <label>Allergens present</label>
                <div className="allergens-grid mt-2">
                  {FSANZ_14.map(allergen => {
                    const isActive = watchAllergens.includes(allergen);
                    return (
                      <button
                        key={allergen}
                        type="button"
                        className={`allergen-chip${isActive ? ' active' : ''}`}
                        onClick={() => handleAllergenToggle(allergen)}
                        aria-pressed={isActive}
                      >
                        {isActive && <span className="chip-check">✓</span>}
                        {allergen}
                      </button>
                    );
                  })}
                </div>
              </div>
            </Col>

            {/* Item Image */}
            <Col xs={12}>
              <Form.Group controlId="item_image" className="mb-3">
                <Form.Label>Item Image (Optional)</Form.Label>
                <div className="file-upload-container d-flex">
                  <label className="file-upload-label">
                    <input
                      type="file"
                      id="item_image"
                      accept="image/jpeg, image/png, image/gif"
                      onChange={handleImageFileChange}
                      className={`file-upload-input ${errors.item_image ? 'is-invalid' : ''}`}
                      style={{ display: 'none' }}
                    />
                    <div><FaUpload className="file-upload-icon" /></div>
                    <div>
                      <span className="file-upload-text">
                        {imagePreview || item?.item_image ? 'Image Selected' : 'Click to select an image'}
                      </span>
                      <span className="format-text">(JPG, PNG, or GIF, max 5MB)</span>
                      {errors.item_image && <div className="error-text mt-1">{errors.item_image.message}</div>}
                    </div>
                  </label>
                </div>
                {(imagePreview || (item?.item_image && !imageFile)) && (
                  <div className="file-preview mt-2">
                    <img
                      src={imagePreview || item?.item_image}
                      alt="Preview"
                      style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', marginRight: '10px', border: '1px solid #ddd' }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                      }}
                    />
                    <div className="d-flex align-items-center">
                      <span className="me-2">{imageFile?.name || 'Current Image'}</span>
                      <Button variant="link" className="text-danger p-0" onClick={removeImage} title="Remove image">
                        <FaTimes />
                      </Button>
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>

            {/* PDF */}
            <Col xs={12}>
              <Form.Group controlId="pdf_file" className="mb-3">
                <Form.Label>PDF Document (Optional)</Form.Label>
                <div className="file-upload-container d-flex">
                  <label className="file-upload-label">
                    <input
                      type="file"
                      id="pdf_file"
                      accept=".pdf,application/pdf"
                      onChange={handlePdfFileChange}
                      className={`file-upload-input ${errors.pdf_file ? 'is-invalid' : ''}`}
                      style={{ display: 'none' }}
                    />
                    <div><FaUpload className="file-upload-icon" /></div>
                    <div>
                      <span className="file-upload-text">
                        {pdfFile?.name || (item?.pdf_url ? 'Current PDF Selected' : 'Click to select a PDF')}
                      </span>
                      <span className="format-text">(PDF only, max 10MB)</span>
                      {errors.pdf_file && <div className="error-text mt-1">{errors.pdf_file.message}</div>}
                    </div>
                  </label>
                </div>
                {(pdfFile || item?.pdf_url) && (
                  <div className="file-preview mt-2">
                    <FaFilePdf className="file-preview-icon" size={24} />
                    <div className="d-flex align-items-center">
                      <span className="me-2">{pdfFile?.name || 'Current PDF File'}</span>
                      {pdfPreview && (
                        <a href={pdfPreview} target="_blank" rel="noopener noreferrer" className="me-2" title="View PDF">
                          <FaEye className="text-primary" />
                        </a>
                      )}
                      <Button variant="link" className="text-danger p-0" onClick={removePdf} title="Remove PDF">
                        <FaTimes />
                      </Button>
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>

            {/* Status */}
            <Col xs={12} className="mt-4">
              <div className="form-control" style={{ padding: '0.75rem 1rem' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <label className="me-2 mb-0" style={{ fontSize: '0.9rem', fontWeight: '500' }}>Status</label>
                    <label className="switch mb-0">
                      <input type="checkbox" {...register('status')} defaultChecked={true} />
                      <span className="slider round"></span>
                    </label>
                    <span className="ms-2" style={{ fontSize: '0.9rem' }}>
                      {watch('status') ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </Col>

            {error && (
              <Col xs={12}>
                <div className="alert alert-danger">{error}</div>
              </Col>
            )}
          </Row>

          <div className="btn-action mt-4">
            <button type="button" className="btn-outline-black" onClick={onHide} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving...</>
              ) : isEditing ? 'Update Item' : 'Add Item'}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddTestKitchenItemModal;
